import { useGetAddressMutation } from "apollo/hooks/mutations";
import {
  COUNTRY_DE,
  FACILITY_COMPANIONSHIP,
  FACILITY_MOBILE_CARE,
  FACILITY_SHORT_TERM_CARE,
  FACILITY_STATIC_CARE,
  SOLUTION_HELP_AT_HOME,
  SOLUTION_MOBILE_CARE,
} from "core/consts";
import { hasAnyFacility } from "core/model/auctions";
import { AdmissionsForm, Careprovider, Solution } from "core/types";
import Translations from "translations/types";
import { MobileAdmissionsFormValues } from "../AddPatientModal/useMobileAdmissionsFormSchema";
import { AdmissionsMobileDesiredCareFormSchema } from "../Form/useAdmissionsDesiredCareForm";

/**
 * Minimum height of the content to ensure the footer sticks to the bottom
 * Full page height (100vh) - Approx. height of header (96px) - Approx. height of footer (104px)
 */
export const MIN_HEIGHT_OF_CONTENT = "min-h-[calc(100vh-96px-104px)]";

export const getStaticFacilityType = (
  facilities: Careprovider["facilities"],
) => {
  const isStaticCare = hasAnyFacility(facilities, [FACILITY_STATIC_CARE]);
  const isShortTermStaticCare = hasAnyFacility(facilities, [
    FACILITY_SHORT_TERM_CARE,
  ]);
  return { isShortTermStaticCare, isStaticCare };
};

export const getMobileFacilityType = (
  facilities: Careprovider["facilities"],
) => {
  const isMobileCare = hasAnyFacility(facilities, [FACILITY_MOBILE_CARE]);
  const isHelpAtHomeCare = hasAnyFacility(facilities, [FACILITY_COMPANIONSHIP]);
  return { isHelpAtHomeCare, isMobileCare };
};

export function getAdmissionsForm(
  careprovider: Careprovider,
  formType: AdmissionsForm["form_type"],
) {
  const { admission_forms } = careprovider;
  return admission_forms?.find((form) => form.form_type === formType);
}

export function getStartOfCareLabel({
  facilities,
  translations,
}: {
  facilities: Careprovider["facilities"];
  translations: Translations;
}) {
  if (!facilities) {
    return translations.careproviderApp.recareAdmissionsForm.desiredCare
      .titleStartDate;
  }

  const { isShortTermStaticCare, isStaticCare } =
    getStaticFacilityType(facilities);

  if (isStaticCare && isShortTermStaticCare)
    return translations.careproviderApp.recareAdmissionsForm.desiredCare
      .titleStartDate;

  if (isShortTermStaticCare)
    return translations.careproviderApp.recareAdmissionsForm.desiredCare
      .titleStartDateShortTermOnly;

  if (isStaticCare)
    return translations.careproviderApp.recareAdmissionsForm.desiredCare
      .titleStartDateStaticOnly;

  return translations.careproviderApp.recareAdmissionsForm.desiredCare
    .titleStartDate;
}

export async function getMobileSearchLocation<
  T extends MobileAdmissionsFormValues | AdmissionsMobileDesiredCareFormSchema,
>(
  auction: T["auction"],
  getAddress: ReturnType<typeof useGetAddressMutation>[0],
) {
  const addressData = await getAddress(auction.profile.search_location);

  if (!addressData?.data?.address) {
    throw new Error("addressData not found in useOnMobileSearchFormSubmit");
  }

  return {
    city: addressData.data.address.city,
    country: COUNTRY_DE, // Default to Germany
    encrypted_house_number:
      auction.profile?.search_location?.encrypted_house_number,
    federal_state: undefined,
    floor: undefined,
    latitude: addressData.data.address.coordinates?.latitude,
    longitude: addressData.data.address.coordinates?.longitude,
    radius_in_meter: 500,
    street: addressData.data.address.street,
    zipcode: addressData.data.address.zipcode,
  };
}

export const getMobileSolutions = (
  facilities: Careprovider["facilities"],
): Solution[] => {
  const { isHelpAtHomeCare, isMobileCare } = getMobileFacilityType(facilities);

  if (isHelpAtHomeCare && isMobileCare) {
    return [SOLUTION_MOBILE_CARE, SOLUTION_HELP_AT_HOME];
  }

  if (isHelpAtHomeCare) {
    return [SOLUTION_HELP_AT_HOME];
  }

  return [SOLUTION_MOBILE_CARE];
};
